/*
 * The shared interactive pieces, by delegation so content added later works too: sheets, side-scrollers, tracks, the jump
 * nav's scroll spy, accordions, the ticker's pause, toasts, days left and Try again. The logic is in small pure functions
 * (tested in node); the wiring below runs only in a browser. Nothing moves when the browser asks for less motion.
 */

/** The site a link opens, when it isn't this one ("instagram.com"); null for this site, mail and phone links. */
export function offSite(href, host) {
  if (!/^https?:\/\//i.test(href || "")) return null;
  let h;
  try { h = new URL(href).hostname.replace(/^www\./, ""); } catch { return null; }
  return h === host.replace(/^www\./, "") ? null : h;
}

/** Every link to another site says so to a screen reader, the same way everywhere ("(opens instagram.com)"). */
function markOffSite(root = document) {
  root.querySelectorAll("a[href^='http']").forEach((a) => {
    const to = offSite(a.getAttribute("href"), location.hostname);
    if (!to || a.dataset.offsite || /\(opens /.test(a.getAttribute("aria-label") || "") || a.querySelector(".sr.opens")) return;
    a.dataset.offsite = to;
    // A named link (an icon button) takes it in its name; any other in hidden words after its own.
    if (a.hasAttribute("aria-label")) a.setAttribute("aria-label", `${a.getAttribute("aria-label")} (opens ${to})`);
    else a.insertAdjacentHTML("beforeend", `<span class="sr opens"> (opens ${to})</span>`);
  });
}

/** Where a sideways scroller should sit so an option is in its middle (never before the start). */
export function centreLeft(boxWidth, optionLeft, optionWidth) { return Math.max(0, optionLeft - (boxWidth - optionWidth) / 2); }

/** Centres an option inside its own track or jump nav. It scrolls only that row, never the page (scrollIntoView would
 *  pull the window to the sticky nav on every scroll). */
function centre(el, smooth = false) {
  const box = el?.closest(".track, .jump");
  if (!box || box.scrollWidth <= box.clientWidth) return;
  const at = el.getBoundingClientRect().left - box.getBoundingClientRect().left + box.scrollLeft;
  box.scrollTo({ left: centreLeft(box.clientWidth, at, el.offsetWidth), behavior: smooth && !still() ? "smooth" : "auto" });
}

/** The next option in a track for a key: arrows move and wrap, Home and End jump; null for any other key. */
export function trackKey(i, key, n) {
  if (key === "ArrowRight" || key === "ArrowDown") return (i + 1) % n;
  if (key === "ArrowLeft" || key === "ArrowUp") return (i - 1 + n) % n;
  if (key === "Home") return 0;
  if (key === "End") return n - 1;
  return null;
}

/** The current section for the jump nav: the last whose top has passed the pinned header (offset). */
export function spyIndex(tops, y, offset = 0) {
  let cur = 0;
  tops.forEach((t, i) => { if (t <= y + offset) cur = i; });
  return cur;
}

/** The scroll spy's line: a section is current once its heading has passed the top third of the window. */
export function spyLine(viewport) { return Math.round(viewport / 3); }

/** A sideways track is at its end (give or take a couple of pixels), so its fade can lift. */
export function atEnd(left, width, scrollWidth) { return left + width >= scrollWidth - 2; }

/** A page worth fetching ahead: a plain page on this site, never the cart, checkout, account, sign-in, apps or a query. */
export function prefetchOk(href, here) {
  let to, from;
  try { to = new URL(href); from = new URL(here); } catch { return false; }
  if (to.origin !== from.origin || to.search || to.hash) return false;
  if (/^\/(cart|checkouts?|account|customer_authentication|apps|services|password|challenge)(\/|$)/.test(to.pathname)) return false;
  return to.pathname !== from.pathname;
}

/** The loading bar finishes on the next page only when this site sent you there in the last few seconds, and not when the
 *  page came instantly (fetched ahead), where there was no bar to finish. */
export function arrived(stamp, now) {
  const t = Number(stamp);
  return stamp != null && Number.isFinite(t) && t > 0 && now - t >= 250 && now - t < 10000;
}

/** Where a page's jump nav sits (Michael, 23 Oct): on a computer it floats at the bottom of the window and steps aside once
 * the footer reaches its line; on a phone it stays in its slot until that reaches the header, then pins under it. */
export function jumpPin({ phone, slotTop, headerBottom, footerTop, viewport }) {
  // A short window (a phone on its side) keeps it in the page: pinned, it would leave a sliver to read.
  if (phone) return { pinned: viewport >= 500 && slotTop <= headerBottom, away: false };
  return { pinned: false, away: footerTop < viewport - 110 };
}

/** A plain click through to another page on this site (Michael, 23 Oct: "it kind of snaps between the two"): the loading
 * cue shows for that, never for a jump on this page, another site, a new tab, a download or a click the page handled. */
export function goingTo(link, e, here) {
  if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
  if ((link.target && link.target !== "_self") || link.hasDownload) return false;
  let to, from;
  try { to = new URL(link.href); from = new URL(here); } catch { return false; }
  if (!/^https?:$/.test(to.protocol) || to.origin !== from.origin) return false;
  return !(to.pathname === from.pathname && to.search === from.search);
}

/** Whole days from today to a date (both YYYY-MM-DD), or null once the date has passed. */
export function daysLeft(to, today) {
  const n = Math.round((new Date(to + "T00:00:00Z") - new Date(today + "T00:00:00Z")) / 86400000);
  return n < 0 ? null : n;
}

/** "13 days left", "1 day left" or "Last day"; null once it's over, so the count is hidden rather than wrong. */
export function daysLeftWords(to, today) {
  const n = daysLeft(to, today);
  return n === null ? null : n === 0 ? "Last day" : `${n} day${n === 1 ? "" : "s"} left`;
}

const still = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Shows a toast for a few seconds. */
/** Messina Eats' year track: Latest shows the newest eight; a year shows that year's events. */
export function eatsShows(pick, year, i) {
  return pick === "Latest" ? i < 8 : year === pick;
}

/** A carousel's count: "01 / 04". */
export function slideCount(i, n) {
  const two = (x) => String(x).padStart(2, "0");
  return `${two(i + 1)} / ${two(n)}`;
}

/** Which photo is in view: the nearest slide to how far the track has scrolled. */
export function slideAt(left, width, gap, n) {
  return Math.max(0, Math.min(n - 1, Math.round(left / (width + gap))));
}

// A story's photo carousel: the arrows move one photo, the count follows the scroll, and an arrow fades at either end.
function carousels(root = document) {
  root.querySelectorAll("[data-carousel]").forEach((c) => {
    if (c.dataset.ready) return;
    c.dataset.ready = "1";
    const track = c.querySelector("[data-ac-track]"), slides = [...track.children], count = c.querySelector("[data-ac-count]");
    const prev = c.querySelector("[data-ac-prev]"), next = c.querySelector("[data-ac-next]");
    const step = () => slides[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || "0");
    const at = () => slideAt(track.scrollLeft, slides[0].getBoundingClientRect().width, parseFloat(getComputedStyle(track).columnGap || "0"), slides.length);
    const paint = () => {
      const i = at();
      count.textContent = slideCount(i, slides.length);
      prev.disabled = i === 0; next.disabled = i === slides.length - 1;
    };
    const smooth = matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    prev.addEventListener("click", () => track.scrollBy({ left: -step(), behavior: smooth }));
    next.addEventListener("click", () => track.scrollBy({ left: step(), behavior: smooth }));
    track.addEventListener("scroll", () => requestAnimationFrame(paint), { passive: true });
    track.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); next.click(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev.click(); }
    });
    if (slides.length < 2) c.querySelector(".ac-foot").hidden = true;
    paint();
  });
}

export function toast(message) {
  const t = document.createElement("div");
  t.className = "toast toast-live";
  t.setAttribute("role", "status");
  t.setAttribute("aria-live", "polite");
  t.textContent = message;
  document.body.append(t);
  setTimeout(() => t.remove(), 4200);
}

// A pinned foot's height, so nothing in a sheet's body can stay under it (the drawer's foot grows with its notes).
const footSize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver((entries) => {
  for (const e of entries) e.target.closest(".sheet-panel")?.style.setProperty("--foot-h", `${Math.ceil(e.target.offsetHeight)}px`);
});
const watchFeet = (sheet) => sheet?.querySelectorAll(".sheet-foot.pinned").forEach((f) => footSize?.observe(f));
if (typeof document !== "undefined") document.addEventListener("ms:sheet-feet", (e) => watchFeet(e.target.closest("ms-sheet")));

/** Everything outside an open sheet goes inert, so Tab and a screen reader can't wander into the page behind it. Each
 * level's siblings, up to the body, are benched; live regions (the toast) stay awake. Returns what it benched. */
export function benchOutside(sheet) {
  const out = [];
  for (let n = sheet; n.parentElement && n.tagName !== "BODY"; n = n.parentElement) {
    for (const sib of n.parentElement.children) {
      if (sib === n || sib.inert || sib.hasAttribute("aria-live") || /^(SCRIPT|STYLE|TEMPLATE|LINK)$/.test(sib.tagName)) continue;
      sib.inert = true;
      out.push(sib);
    }
  }
  return out;
}
let benched = [];
const bench = (top) => { benched.forEach((n) => { n.inert = false; }); benched = top ? benchOutside(top) : []; };

// Open sheets, newest last: a sheet opened from another (Change store from the tub sheet) sits above it, and the one
// under it goes inert until it's back on top.
const openSheets = [];
const MsSheet = typeof HTMLElement === "undefined" ? null : class extends HTMLElement {
  open(opener) {
    // Opened again while it's open (refilled), it keeps the control that first opened it.
    this.opener = opener || (!this.hidden && this.opener) || document.activeElement;
    const under = openSheets.at(-1);
    if (under && under !== this) under.inert = true;
    if (!openSheets.includes(this)) openSheets.push(this);
    this.style.zIndex = String(60 + openSheets.length);
    this.inert = false;
    this.hidden = false;
    bench(this);
    if (under && under !== this) under.inert = true;
    document.documentElement.classList.add("sheet-open");
    watchFeet(this);
    (this.querySelector("[autofocus], input, button:not([data-close]), [href]") || this.querySelector("[data-close]"))?.focus();
    this.dispatchEvent(new CustomEvent("ms:sheet-open", { bubbles: true }));
  }
  close() {
    this.hidden = true;
    const i = openSheets.indexOf(this);
    if (i >= 0) openSheets.splice(i, 1);
    this.style.zIndex = "";
    const top = openSheets.at(-1);
    bench(top);
    if (top) top.inert = false;
    else document.documentElement.classList.remove("sheet-open");
    this.opener?.focus?.();
    this.dispatchEvent(new CustomEvent("ms:sheet-close", { bubbles: true }));
  }
  connectedCallback() {
    this.addEventListener("click", (e) => { if (e.target === this || e.target.closest("[data-close]")) this.close(); });
    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
      if (e.key !== "Tab") return;
      const f = [...this.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex='0']")].filter((x) => x.offsetParent !== null);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f.at(-1).focus(); }
      else if (!e.shiftKey && document.activeElement === f.at(-1)) { e.preventDefault(); f[0].focus(); }
    });
  }
};

function selectTrack(track, i) {
  const opts = [...track.querySelectorAll('[role="radio"]')];
  opts.forEach((o, k) => { o.setAttribute("aria-checked", String(k === i)); o.tabIndex = k === i ? 0 : -1; o.classList.toggle("on", k === i); });
  track.dispatchEvent(new CustomEvent("ms:track", { bubbles: true, detail: { index: i, value: opts[i]?.dataset.value ?? opts[i]?.textContent.trim() } }));
  return opts[i];
}

/** A page's jump nav: floats at the bottom on a computer, pins under the header on a phone (jumpPin). */
function pinJump(pin) {
  const foot = document.querySelector(".ft"), hd = document.querySelector(".hd"), phone = matchMedia("(max-width: 767px)");
  const place = () => {
    const headerBottom = hd ? Math.max(0, hd.getBoundingClientRect().bottom) : 0;
    const at = jumpPin({ phone: phone.matches, slotTop: pin.getBoundingClientRect().top, headerBottom, footerTop: foot ? foot.getBoundingClientRect().top : Infinity, viewport: innerHeight });
    pin.style.setProperty("--pin-top", `${Math.round(headerBottom)}px`);
    pin.classList.toggle("pinned", at.pinned);
    if (phone.matches) document.documentElement.style.scrollPaddingTop = at.pinned && dock ? `${Math.round(headerBottom + dock.offsetHeight + 12)}px` : "";
    pin.classList.toggle("away", at.away);
    // stepped aside for the footer, it leaves the tab order too
    if (dock) dock.inert = at.away;
  };
  const dock = pin.querySelector(".jump-dock"), track = pin.querySelector(".jump");
  // The track's fade lifts at its end, and whatever has focus in it scrolls into view (the track only, never the page).
  const ends = () => track && track.classList.toggle("at-end", atEnd(track.scrollLeft, track.clientWidth, track.scrollWidth));
  track?.addEventListener("scroll", ends, { passive: true });
  track?.addEventListener("focusin", (e) => { centre(e.target.closest("a"), true); pin.classList.remove("hush"); });
  // On a computer it slides away while you scroll down and comes back when you scroll up, so it never sits on what you're
  // reading or reaching for.
  let raf, lastY = scrollY;
  const later = () => {
    cancelAnimationFrame(raf); raf = requestAnimationFrame(place);
    if (phone.matches) return;
    if (scrollY > lastY + 4 && !dock?.contains(document.activeElement)) pin.classList.add("hush");
    else if (scrollY < lastY - 4) pin.classList.remove("hush");
    lastY = scrollY;
  };
  addEventListener("scroll", later, { passive: true });
  addEventListener("resize", () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => { place(); ends(); }); });
  place(); ends();
}

/** The loading cue: a blood orange bar along the header's bottom edge from the click until the next page shows, where it
 *  runs to the end and fades. Screen readers never hear it. A page you point at, touch or tab to is fetched ahead. */
function going() {
  const root = document.documentElement, KEY = "ms-going";
  let bar;
  const show = () => {
    bar ??= Object.assign(document.body.appendChild(document.createElement("div")), { className: "page-going", ariaHidden: "true" });
    const hd = document.querySelector(".hd");
    bar.style.top = `${hd ? Math.max(0, Math.round(hd.getBoundingClientRect().bottom) - 3) : 0}px`;
  };
  const store = (v) => { try { v ? sessionStorage.setItem(KEY, v) : sessionStorage.removeItem(KEY); } catch {} };
  document.addEventListener("click", (e) => {
    const a = e.target.closest?.("a[href]");
    if (!a || !goingTo({ href: a.href, target: a.target, hasDownload: a.hasAttribute("download") }, e, location.href)) return;
    show(); root.classList.remove("is-arrived"); root.classList.add("is-going");
    store(String(Date.now()));
    clearTimeout(going.t); going.t = setTimeout(() => { root.classList.remove("is-going"); store(""); }, 8000);
  });
  // Here from a click on the last page: the bar finishes and fades.
  let stamp = null;
  try { stamp = sessionStorage.getItem(KEY); } catch {}
  if (arrived(stamp, Date.now())) {
    show(); root.classList.add("is-arrived");
    setTimeout(() => root.classList.remove("is-arrived"), 900);
  }
  store("");
  // Back to a page from the browser's cache: it's here, so the cue goes.
  addEventListener("pageshow", () => root.classList.remove("is-going"));
  // Fetched ahead on keyboard focus (pointing and touching are the speculation rules' job).
  const ahead = new Set();
  document.addEventListener("focusin", (e) => {
    const a = e.target.closest?.("a[href]");
    if (!a || ahead.has(a.href) || !prefetchOk(a.href, location.href)) return;
    ahead.add(a.href);
    document.head.appendChild(Object.assign(document.createElement("link"), { rel: "prefetch", href: a.href }));
  });
}

function spy(nav) {
  // The links are read each time, so a nav refilled after a live read (a store change) is followed too.
  const mark = () => {
    const links = [...nav.querySelectorAll('a[href^="#"]')];
    const targets = links.map((a) => document.getElementById(a.getAttribute("href").slice(1)));
    if (!links.length || targets.some((t) => !t)) return;
    const i = spyIndex(targets.map((t) => t.getBoundingClientRect().top + scrollY), scrollY, spyLine(innerHeight));
    links.forEach((a, k) => { a.classList.toggle("on", k === i); if (k === i) a.setAttribute("aria-current", "location"); else a.removeAttribute("aria-current"); });
    centre(links[i], true);
  };
  if (!nav.spied) {
    nav.spied = true;
    let raf;
    addEventListener("scroll", () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(mark); }, { passive: true });
  }
  mark();
}

function railFor(foot) {
  const rail = foot.previousElementSibling?.matches(".cab-rail") ? foot.previousElementSibling : foot.closest("section, .cab")?.querySelector(".cab-rail");
  return rail;
}

/** A hero's ring words shrink to go round once, so the last separator meets the first word instead of being cut off. */
function fitRings() {
  document.querySelectorAll(".spin text").forEach((t) => {
    const room = 2 * Math.PI * 50 - 2;
    t.setAttribute("font-size", "13.5");
    t.setAttribute("letter-spacing", "2.6");
    const len = t.getComputedTextLength();
    if (len > room) { const k = room / len; t.setAttribute("font-size", (13.5 * k).toFixed(2)); t.setAttribute("letter-spacing", (2.6 * k).toFixed(2)); }
  });
}

/** Photos fade in once they've loaded (a late one waits on a light shimmer, ms-layout.css); a broken one shows too. */
function photos() {
  document.documentElement.classList.add("js-photos");
  const settle = (e) => { if (e.target?.tagName === "IMG") e.target.classList.add("in"); };
  document.addEventListener("load", settle, true);
  document.addEventListener("error", settle, true);
  for (const i of document.querySelectorAll(".face > img, .ph-img > img")) if (i.complete) i.classList.add("in");
}

// A sticky phone bar with data-after waits until that element (the panel's own button) has scrolled up out of view.
function barsAfter() {
  if (!("IntersectionObserver" in window)) return;
  document.querySelectorAll(".orderbar[data-after]").forEach((bar) => {
    const el = document.getElementById(bar.dataset.after);
    if (!el) return;
    bar.classList.add("bar-wait");
    new IntersectionObserver(([e]) => bar.classList.toggle("bar-wait", e.isIntersecting || e.boundingClientRect.top > 0)).observe(el);
  });
}

function boot() {
  barsAfter();
  photos();
  carousels();
  document.addEventListener("ms:track", (e) => {
    if (e.target.dataset?.name !== "eats-year") return;
    const wall = e.target.closest("section")?.querySelector(".x-wall");
    wall?.querySelectorAll(".x-ev").forEach((c) => { c.hidden = !eatsShows(e.detail.value, c.dataset.year, Number(c.dataset.i)); });
  });
  document.fonts?.ready.then(fitRings);
  // A dropdown built on <details> (the collection's Sort) closes on Escape and hands focus back to its button.
  document.addEventListener("keydown", (e) => {
    const d = e.key === "Escape" && e.target.closest?.("details[open]");
    if (d) { d.open = false; d.querySelector("summary")?.focus(); }
  });
  // A heading's arrows show only while the side-scroller under them has more than fits.
  const fitArrows = () => document.querySelectorAll(".rail-hd").forEach((r) => { const rail = railFor(r); r.hidden = !rail || rail.scrollWidth <= rail.clientWidth + 2; });
  fitArrows();
  addEventListener("resize", fitArrows, { passive: true });
  if (!customElements.get("ms-sheet")) customElements.define("ms-sheet", MsSheet);
  // A jump nav filled after a live read (the Order page's groups) asks to be followed once it has its links.
  document.addEventListener("ms:jump", (e) => spy(e.target));

  // One video plays at a time: starting one pauses the rest, so two never talk over each other.
  document.addEventListener("play", (e) => {
    if (!(e.target instanceof HTMLVideoElement)) return;
    for (const other of document.querySelectorAll("video")) if (other !== e.target && !other.paused && !other.muted) other.pause();
  }, true);

  document.addEventListener("click", (e) => {
    // A video card's play button: the video takes the poster's place in the sticker and plays.
    const vp = e.target.closest(".vplay");
    if (vp) {
      const card = vp.closest(".vcard");
      const tpl = card && card.querySelector("template");
      const face = card && card.querySelector(".face");
      if (tpl && face) {
        face.replaceChildren(tpl.content.cloneNode(true));
        const v = face.querySelector("video");
        if (v) { v.focus(); v.play().catch(() => {}); }
      }
      return;
    }

    // "Start a chat" buttons, and any link to #chat: open our chat (chat.gelatomessina.com) where it's loaded.
    const chat = e.target.closest("[data-chat-open], a[href='#chat']");
    if (chat && window.MessinaChat) { e.preventDefault(); window.MessinaChat.open(); return; }

    const opener = e.target.closest("[data-sheet-open]");
    if (opener) { e.preventDefault(); document.getElementById(opener.dataset.sheetOpen)?.open(opener); return; }

    const radio = e.target.closest('.track [role="radio"]');
    if (radio) { const track = radio.closest(".track"); selectTrack(track, [...track.querySelectorAll('[role="radio"]')].indexOf(radio)); return; }

    const row = e.target.closest(".acc-row");
    if (row) {
      const open = row.getAttribute("aria-expanded") !== "true";
      row.setAttribute("aria-expanded", String(open));
      row.closest(".acc-item")?.classList.toggle("open", open);
      const body = document.getElementById(row.getAttribute("aria-controls"));
      if (body) body.hidden = !open;
      return;
    }

    const step = e.target.closest("[data-rail-prev], [data-rail-next]");
    if (step) {
      const rail = railFor(step.closest(".rail-foot"));
      const card = rail?.querySelector(".row > *");
      if (rail && card) rail.scrollBy({ left: (step.matches("[data-rail-next]") ? 1 : -1) * (card.getBoundingClientRect().width + 24), behavior: still() ? "auto" : "smooth" });
      return;
    }

    const retry = e.target.closest("[data-retry]");
    if (retry) retry.dispatchEvent(new CustomEvent("ms:retry", { bubbles: true }));
  });

  document.addEventListener("keydown", (e) => {
    // Any other radio group (the cake's days, the vote board's rounds) moves the same way: arrows choose the next one.
    const other = e.target.closest?.('[role="radiogroup"]:not(.track) [role="radio"]');
    if (other) {
      const group = other.closest('[role="radiogroup"]');
      const opts = [...group.querySelectorAll('[role="radio"]:not([aria-disabled="true"]):not([disabled])')];
      const at = trackKey(opts.indexOf(other), e.key, opts.length);
      if (at === null) return;
      e.preventDefault();
      opts[at].click();
      (group.querySelector('[role="radio"][aria-checked="true"]') || opts[at]).focus();
      return;
    }
    const radio = e.target.closest?.('.track [role="radio"]');
    if (!radio) return;
    const track = radio.closest(".track");
    const opts = [...track.querySelectorAll('[role="radio"]')];
    const next = trackKey(opts.indexOf(radio), e.key, opts.length);
    if (next === null) { if (e.key === " " || e.key === "Enter") { e.preventDefault(); selectTrack(track, opts.indexOf(radio)); } return; }
    e.preventDefault();
    selectTrack(track, next).focus();
    const target = opts[next];
    centre(target);
  });

  // With reduced motion the ticker is one line you swipe; a keyboard can reach and scroll it too (it's a named region).
  if (still()) document.querySelectorAll(".ticker").forEach((t) => { t.classList.add("paused"); t.tabIndex = 0; });
  document.querySelectorAll(".jump").forEach(spy);
  document.querySelectorAll("[data-jump-pin]").forEach(pinJump);
  going();
  // A track opens with its current choice centred, and never wraps.
  document.querySelectorAll(".track, .jump").forEach((t) => centre(t.querySelector('[aria-checked="true"], .on')));

  paintDaysLeft();
  markOffSite();
  // Tabbing along a sideways scroller (a rail, the About pills, a date run) brings the focused item fully into view, past
  // the faded edge, so its ring is never cut off.
  document.addEventListener("focusin", (e) => {
    const t = e.target;
    if (!(t instanceof Element) || !t.closest(SCROLLERS)) return;
    t.scrollIntoView({ inline: "nearest", block: "nearest" });
  });
    // The theme editor reloads a section on its own when staff change it: count its days and fit its arrows again.
  document.addEventListener("shopify:section:load", () => { paintDaysLeft(); fitArrows(); markOffSite(); fitRings(); });
}

const SCROLLERS = ".cab-rail, .jump, .dates-run, .coll-cats, .faq-toc, .cr-pills, .rail, .tdays";

function paintDaysLeft() {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Sydney", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  document.querySelectorAll("[data-days-left]").forEach((el) => {
    const to = el.dataset.daysLeft;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(to)) return;
    const words = daysLeftWords(to, today);
    if (words) el.querySelector("span").textContent = words;
    el.hidden = !words;
  });
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
}
